import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  HiOutlineArrowPath,
  HiOutlineMagnifyingGlass,
  HiOutlineShare,
  HiOutlineDocumentText,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineExclamationCircle,
  HiOutlineMagnifyingGlassCircle,
  HiOutlineBookOpen,
  HiOutlineChevronDown,
  HiOutlineLanguage,
  HiOutlineGlobeAlt,
  HiChevronLeft,
  HiChevronRight,
} from "react-icons/hi2";
import RecipeCard from "../../components/cards/RecipeCard.jsx";
import { useLanguage } from "../../contexts/LanguageContext";
import { useRecipesTranslation } from "../../components/common/RecipeTranslator";
import { useRecipePopup } from "../../contexts/RecipePopupContext";
import shareService from "../../services/ShareService.js";
import API from "../../config/api.js";
import { foodBg } from "../../assets/images";
import { countries } from "../user/dashboard/utils/countries.js";
import { CardGridLoading } from "../../components/common/CardGridLoading";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
};

const PER_PAGE = 9;

/**
 * Page des recettes partagées
 * Affiche les recettes que les utilisateurs ont partagées avec la communauté
 */
const SharedRecipesPage = ({ user, token }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { openEditPopup } = useRecipePopup();
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [filterCountry, setFilterCountry] = useState("");
  const [page, setPage] = useState(1);

  const loadSharedRecipes = useCallback(
    async (forceRefresh = false) => {
      try {
        setLoading(true);
        setError(null);
        const data = await shareService.getSharedRecipes(forceRefresh);
        setRecipes(data?.data || data || []);
      } catch (err) {
        console.error("Erreur lors du chargement des recettes partagées:", err);
        setError(t("connectionError"));
        setRecipes([]);
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    loadSharedRecipes();
  }, [loadSharedRecipes]);

  const { translatedRecipes, isTranslating } = useRecipesTranslation(recipes);

  /** Filtrage côté client : recherche (titre, description) + difficulté */
  const filteredRecipes = React.useMemo(() => {
    let list = translatedRecipes;
    const term = (searchTerm || "").trim().toLowerCase();
    if (term) {
      list = list.filter(
        (r) =>
          (r.title || "").toLowerCase().includes(term) ||
          (r.name || "").toLowerCase().includes(term) ||
          (r.description || "").toLowerCase().includes(term),
      );
    }
    if (filterDifficulty && filterDifficulty !== "all") {
      list = list.filter(
        (r) =>
          (r.difficulty || "").toLowerCase() === filterDifficulty.toLowerCase(),
      );
    }
    if (filterCountry) {
      const countryOption = countries.find((c) => c.code === filterCountry);
      const translatedName = countryOption ? t(countryOption.nameKey) : "";
      const codeLower = filterCountry.trim().toLowerCase();
      const nameLower = (translatedName || "").trim().toLowerCase();
      list = list.filter((r) => {
        const rc = (r.country || "").trim().toLowerCase();
        return rc === codeLower || (nameLower && rc === nameLower);
      });
    }
    return list;
  }, [translatedRecipes, searchTerm, filterDifficulty, filterCountry]);

  const totalFiltered = filteredRecipes.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PER_PAGE));
  const paginatedRecipes = React.useMemo(() => {
    const start = (page - 1) * PER_PAGE;
    return filteredRecipes.slice(start, start + PER_PAGE);
  }, [filteredRecipes, page]);

  /** Remettre à la page 1 quand recherche ou filtre change */
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterDifficulty, filterCountry]);

  const handleFavoriteToggle = async (recipeId, isFavorite) => {
    if (!user) {
      alert(t("mustBeLoggedIn"));
      return;
    }
    try {
      const url = isFavorite
        ? `${API.baseURL}/favorites/remove/${recipeId}`
        : `${API.baseURL}/favorites/add/${recipeId}`;
      const response = await fetch(url, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        // Optionnel: rafraîchir la liste si besoin
      } else {
        alert(t("errorManagingFavorites"));
      }
    } catch (err) {
      console.error("Erreur favoris:", err);
      alert(t("errorManagingFavorites"));
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden">
      <div
        className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat bg-slate-900"
        style={{ backgroundImage: `url(${foodBg})` }}
        aria-hidden
      />
      <div className="fixed inset-0 z-0 bg-slate-900/40" aria-hidden />

      <div className="relative z-10 w-full min-w-0 py-4 sm:py-8 md:py-12 lg:py-16">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            className="relative bg-white dark:bg-gray-800/50 backdrop-blur-lg rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-xl border border-slate-200/50 dark:border-slate-600/50 p-4 sm:p-6 lg:p-8 mb-4 sm:mb-8"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="absolute left-0 top-0 bottom-0 w-1 sm:w-2 bg-gradient-to-b from-orange-500 to-amber-500 rounded-r-full" />
            <div className="pl-3 sm:pl-5 lg:pl-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-6 mb-3 sm:mb-4">
                <div className="flex-1 min-w-0">
                  <motion.div
                    className="flex items-center gap-3 sm:gap-4"
                    variants={itemVariants}
                  >
                    <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center text-white shadow-lg ring-2 ring-white/20">
                      <HiOutlineShare className="w-6 h-6 sm:w-7 sm:h-7" aria-hidden />
                    </div>
                    <div className="min-w-0">
                      <p className="text-orange-300/90 text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-0.5">
                        {t("community")}
                      </p>
                      <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-ink-900 dark:text-white flex flex-wrap items-center gap-2">
                        <span className="break-words">{t("sharedRecipes")}</span>
                        {isTranslating && (
                          <motion.span
                            className="inline-flex items-center gap-1 sm:gap-1.5 text-xs sm:text-sm font-normal text-amber-200/90"
                            animate={{ opacity: [1, 0.6, 1] }}
                            transition={{ duration: 1.5, repeat: Infinity }}
                          >
                            <HiOutlineLanguage className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                            {t("translating")}
                          </motion.span>
                        )}
                      </h1>
                      <p className="text-ink-600 dark:text-white/80 text-xs sm:text-sm lg:text-base mt-0.5 sm:mt-1">
                        {t("discoverCreations")}
                      </p>
                    </div>
                  </motion.div>
                </div>
                <motion.div
                  className="flex items-center gap-2 sm:gap-4 flex-shrink-0"
                  variants={itemVariants}
                >
                  <div className="flex items-center gap-2 sm:gap-3 bg-ink-50 dark:bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-3 border border-ink-900/10 dark:border-white/20">
                    <HiOutlineDocumentText
                      className="w-5 h-5 sm:w-6 sm:h-6 text-orange-300 flex-shrink-0"
                      aria-hidden
                    />
                    <div className="text-left">
                      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-ink-900 dark:text-white leading-tight tabular-nums">
                        {totalFiltered}
                      </div>
                      <div className="text-[10px] sm:text-xs text-ink-600 dark:text-white/80">
                        {t("recipes")}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => loadSharedRecipes(true)}
                    disabled={loading}
                    className="inline-flex items-center justify-center gap-1.5 sm:gap-2 p-2.5 sm:px-4 sm:py-3 bg-ink-100 hover:bg-ink-200 border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20 dark:text-white font-medium rounded-lg sm:rounded-xl transition-all disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:ring-offset-2 focus:ring-offset-transparent touch-manipulation"
                    aria-label={t("refreshSharedRecipes")}
                  >
                    <HiOutlineArrowPath
                      className={`w-5 h-5 flex-shrink-0 ${loading ? "animate-spin" : ""}`}
                      aria-hidden
                    />
                    <span className="hidden sm:inline text-sm">
                      {t("refreshSharedRecipes")}
                    </span>
                  </button>
                </motion.div>
              </div>

              {/* Barre de recherche et filtres - stack sur mobile */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mt-4 sm:mt-5 pt-4 sm:pt-5 border-t border-white/15">
                <div className="flex-1 relative min-w-0">
                  <HiOutlineMagnifyingGlass
                    className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                    aria-hidden
                  />
                  <input
                    type="text"
                    placeholder={t("searchRecipe")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-600 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 text-slate-800 dark:text-slate-100 placeholder-slate-400 text-sm transition-all shadow-sm"
                  />
                </div>
                <div className="w-full sm:w-48 lg:w-52 flex-shrink-0 relative">
                  <HiOutlineAdjustmentsHorizontal
                    className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                    aria-hidden
                  />
                  <select
                    value={filterDifficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-9 sm:pr-10 py-2.5 sm:py-3 bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-600 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 text-slate-800 dark:text-slate-100 text-xs sm:text-sm transition-all appearance-none cursor-pointer shadow-sm"
                  >
                    <option value="all">{t("allDifficulties")}</option>
                    <option value="Facile">{t("easy")}</option>
                    <option value="Moyen">{t("medium")}</option>
                    <option value="Difficile">{t("hard")}</option>
                  </select>
                  <HiOutlineChevronDown
                    className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none"
                    aria-hidden
                  />
                </div>
                <div className="w-full sm:w-48 lg:w-52 flex-shrink-0 relative">
                  <HiOutlineGlobeAlt
                    className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none z-10"
                    aria-hidden
                  />
                  <select
                    value={filterCountry}
                    onChange={(e) => setFilterCountry(e.target.value)}
                    className="w-full pl-10 sm:pl-12 pr-9 sm:pr-10 py-2.5 sm:py-3 bg-white dark:bg-slate-800/90 border border-slate-200 dark:border-slate-600 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 text-slate-800 dark:text-slate-100 text-xs sm:text-sm transition-all appearance-none cursor-pointer shadow-sm"
                  >
                    {countries.map((c) => (
                      <option key={c.code || "all"} value={c.code}>
                        {t(c.nameKey)}
                      </option>
                    ))}
                  </select>
                  <HiOutlineChevronDown
                    className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-slate-400 pointer-events-none"
                    aria-hidden
                  />
                </div>
              </div>
            </div>
          </motion.div>

          {loading || (isTranslating && recipes.length > 0) ? (
            <motion.div
              className="bg-white dark:bg-gray-800/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-xl border border-ink-900/10 dark:border-slate-600/50 p-4 sm:p-8 lg:p-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <CardGridLoading
                message={t("loadingRecipes")}
                gridCols="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                count={6}
              />
            </motion.div>
          ) : error ? (
            <motion.div
              className="bg-white dark:bg-gray-800/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-xl border border-ink-900/10 dark:border-slate-600/50 p-6 sm:p-12 lg:p-16"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="flex flex-col items-center text-center max-w-md mx-auto px-2">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4 sm:mb-6">
                  <HiOutlineExclamationCircle
                    className="w-8 h-8 sm:w-10 sm:h-10 text-red-400"
                    aria-hidden
                  />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-ink-900 dark:text-white mb-2">
                  {t("error")}
                </h3>
                <p className="text-ink-600 dark:text-white/80 text-xs sm:text-sm mb-4 sm:mb-6">{error}</p>
                <button
                  onClick={() => loadSharedRecipes(true)}
                  className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold text-sm sm:text-base hover:from-orange-600 hover:to-amber-600 shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/50 touch-manipulation"
                >
                  <HiOutlineArrowPath className="w-5 h-5 shrink-0" />
                  {t("retry")}
                </button>
              </div>
            </motion.div>
          ) : translatedRecipes.length > 0 ? (
            <>
              {filteredRecipes.length > 0 ? (
                <>
                  <motion.div
                    className="grid grid-cols-1 min-[520px]:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 lg:gap-6 items-stretch"
                    initial={false}
                    animate={{ opacity: 1 }}
                  >
                    {paginatedRecipes.map((recipe) => (
                      <div
                        key={recipe.id ?? recipe.title ?? String(Math.random())}
                        className="h-full w-full"
                      >
                        <RecipeCard
                          recipe={recipe}
                          user={user}
                          showShareForAll
                          showAuthor
                          onFavoriteToggle={handleFavoriteToggle}
                          onEdit={
                            user
                              ? (r) => {
                                  openEditPopup(r, () =>
                                    loadSharedRecipes(true),
                                  );
                                }
                              : undefined
                          }
                        />
                      </div>
                    ))}
                  </motion.div>

                  {/* Pagination - compact sur mobile */}
                  <motion.div
                    className="mt-6 sm:mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-white hover:bg-ink-100 border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm touch-manipulation"
                    >
                      <HiChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                      <span>{t("previous") || "Précédent"}</span>
                    </button>
                    <span className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-white border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:border-white/20 dark:text-white text-xs sm:text-sm font-medium min-w-[5rem] sm:min-w-[7rem] text-center tabular-nums">
                      {t("page") || "Page"} {page} / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                      disabled={page >= totalPages}
                      className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg sm:rounded-xl bg-white hover:bg-ink-100 border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium text-sm touch-manipulation"
                    >
                      <span>{t("next") || "Suivant"}</span>
                      <HiChevronRight className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" />
                    </button>
                  </motion.div>
                  {totalFiltered > 0 && (
                    <p className="mt-2 sm:mt-3 text-center text-white/70 text-xs sm:text-sm px-2">
                      {totalFiltered} {t("recipes")}{" "}
                      {totalPages > 1
                        ? ` · ${t("page") || "Page"} ${page}`
                        : ""}
                    </p>
                  )}
                </>
              ) : (
                <motion.div
                  className="bg-white dark:bg-gray-800/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-xl border border-ink-900/10 dark:border-slate-600/50 p-6 sm:p-12 lg:p-16"
                  initial={{ opacity: 0, scale: 0.98 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <div className="flex flex-col items-center text-center max-w-md mx-auto px-2">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-slate-500/20 flex items-center justify-center mb-4 sm:mb-6">
                      <HiOutlineMagnifyingGlassCircle
                        className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400"
                        aria-hidden
                      />
                    </div>
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-ink-900 dark:text-white mb-2">
                      {t("noRecipeFound")}
                    </h3>
                    <p className="text-ink-600 dark:text-white/80 text-xs sm:text-sm mb-4 sm:mb-6">
                      {t("noRecipeMatches")}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm("");
                        setFilterDifficulty("all");
                        setFilterCountry("");
                      }}
                      className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-white hover:bg-ink-100 border border-ink-900/10 text-ink-700 dark:bg-white/10 dark:hover:bg-white/20 dark:border-white/20 dark:text-white rounded-lg sm:rounded-xl font-medium text-sm transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/50 touch-manipulation"
                    >
                      <HiOutlineAdjustmentsHorizontal className="w-4 h-4 shrink-0" />
                      {t("resetButton")}
                    </button>
                  </div>
                </motion.div>
              )}
            </>
          ) : (
            <motion.div
              className="bg-white dark:bg-gray-800/30 backdrop-blur-md rounded-xl sm:rounded-2xl shadow-xl border border-ink-900/10 dark:border-slate-600/50 p-6 sm:p-12 lg:p-16"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <div className="flex flex-col items-center text-center max-w-md mx-auto px-2">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-orange-500/20 flex items-center justify-center mb-4 sm:mb-6">
                  <HiOutlineBookOpen
                    className="w-8 h-8 sm:w-10 sm:h-10 text-orange-400"
                    aria-hidden
                  />
                </div>
                <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-ink-900 dark:text-white mb-2">
                  {t("noSharedRecipes")}
                </h3>
                <p className="text-ink-600 dark:text-white/80 text-xs sm:text-sm mb-4 sm:mb-6">
                  {t("discoverCreations")}
                </p>
                <button
                  onClick={() => navigate("/recipes")}
                  className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold text-sm sm:text-base hover:from-orange-600 hover:to-amber-600 shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-orange-500/50 touch-manipulation"
                >
                  <HiOutlineBookOpen className="w-5 h-5 shrink-0" />
                  {t("discoverRecipes")}
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharedRecipesPage;
